import { localId } from '@/lib/appStorage';

export interface NoteVersion {
  at: string;
  content: string;
}

export type PeriodType = 'semester' | 'trimester' | 'year' | 'other';

export interface Note {
  id: string;
  notebookId: string;
  title: string;
  content: string;
  favorite: boolean;
  important: boolean;
  toReview: boolean;
  updatedAt: string;
  versions: NoteVersion[];
}

export interface Notebook {
  id: string;
  name: string;
  year: string;
  periodType: PeriodType;
  periodLabel: string;
  subject: string;
  /** Carnet fermé : il reste consultable mais sort de la liste active. */
  closed: boolean;
  color: string;
  createdAt: string;
}

export interface NotesState {
  notebooks: Notebook[];
  notes: Note[];
}

/** État de départ volontairement vide : l'utilisateur crée son propre espace. */
export const EMPTY_NOTES: NotesState = { notebooks: [], notes: [] };

export const NOTEBOOK_COLORS = [
  'bg-accent-100 text-accent-800',
  'bg-secondary-100 text-secondary-900',
  'bg-primary-100 text-primary-800',
  'bg-background-200 text-foreground-800',
  'bg-emerald-100 text-emerald-800',
  'bg-amber-100 text-amber-800',
];

export function createNote(notebookId: string, title = ''): Note {
  const now = new Date().toISOString();
  return {
    id: localId('note'),
    notebookId,
    title,
    content: '',
    favorite: false,
    important: false,
    toReview: false,
    updatedAt: now,
    versions: [],
  };
}

export function createNotebook(params: {
  name: string;
  year: string;
  periodType: PeriodType;
  periodLabel: string;
  subject: string;
}): Notebook {
  return {
    id: localId('nb'),
    name: params.name,
    year: params.year,
    periodType: params.periodType,
    periodLabel: params.periodLabel,
    subject: params.subject,
    closed: false,
    color: NOTEBOOK_COLORS[Math.floor(Math.random() * NOTEBOOK_COLORS.length)],
    createdAt: new Date().toISOString(),
  };
}

export function periodIcon(type: PeriodType): string {
  switch (type) {
    case 'semester':
      return 'ri-calendar-2-line';
    case 'trimester':
      return 'ri-calendar-line';
    case 'year':
      return 'ri-calendar-check-line';
    default:
      return 'ri-price-tag-3-line';
  }
}

export function splitSentences(text: string): string[] {
  return text
    .replace(/\n+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 25);
}

/** Résumé extractif local : fonctionne sans réseau. */
export function summarizeNotes(notes: Note[], maxPoints = 7): string[] {
  const sentences = notes.flatMap((note) => splitSentences(note.content));
  const scored = new Map<string, number>();
  const stop = new Set([
    'le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'en', 'à', 'au', 'aux', 'pour', 'par',
    'est', 'sont', 'que', 'qui', 'dans', 'sur', 'avec', 'plus', 'ce', 'cette', 'il', 'elle', 'on',
  ]);
  sentences.forEach((sentence) => {
    const words = sentence.toLowerCase().match(/[a-zà-ÿ]{4,}/g) || [];
    let score = 0;
    words.forEach((w) => {
      if (!stop.has(w)) score += 1;
    });
    scored.set(sentence, score / Math.max(6, words.length) + (scored.get(sentence) || 0));
  });
  return Array.from(scored.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxPoints)
    .map(([sentence]) => sentence);
}

/** Texte brut compact d'une note (utile pour les quiz et l'IA). */
export function noteToPlainText(note: Note): string {
  return `${note.title}\n${note.content}`.trim();
}